"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DisclaimerManagerV1Impl = void 0;
const events_1 = require("events");
const next_id_1 = require("./next-id");
class DisclaimerManagerV1Impl {
    constructor(scope, options) {
        this.eventEmitter = new events_1.EventEmitter();
        this.state = {
            referencedDisclaimers: {},
            globalDisclaimers: {},
        };
        this.scope = scope;
        this.defaultLegalEntity = options.defaultLegalEntity;
        this.legalEntityLabels = options.legalEntityLabels;
        this.enabledAsyncEvent = Boolean(options.enabledAsyncEvent);
        this.sanitize = options.sanitize || ((text) => text);
    }
    subscribe(listener, type) {
        const eventName = type ? `update-${type}` : 'update';
        this.eventEmitter.addListener(eventName, listener);
        return {
            unsubscribe: () => this.eventEmitter.removeListener(eventName, listener),
        };
    }
    registerGlobalDisclaimer(text, legalEntity) {
        this.registerDisclaimer(text, 'global', undefined, undefined, legalEntity);
    }
    registerReferenceDisclaimer(refKind, text, namedReference, legalEntity) {
        const dis = this.registerDisclaimer(text, 'reference', refKind, namedReference, legalEntity);
        return namedReference && dis.namedReference && dis.namedReference.length > 0
            ? dis.namedReference[0]
            : dis.reference;
    }
    registerDisclaimer(text, type, refKind, namedReference, legalEntity) {
        const display = this.isTypeDisplayableInFooter(type, refKind);
        const isGlobal = type === 'global';
        const isNamed = namedReference !== undefined;
        const sanitizedText = this.sanitize(text);
        const key = this.convertToKey(legalEntity || this.defaultLegalEntity, sanitizedText, type, refKind);
        const existing = isGlobal
            ? this.state.globalDisclaimers[key]
            : this.state.referencedDisclaimers[key];
        // Get or create disclaimer object
        let disclaimer = existing
            ? Object.assign({}, existing) : {
            defaultReferenceCount: 0,
            referenceCount: {},
            type,
            refKind,
            namedReference,
            text: sanitizedText,
            legalEntity: legalEntity || this.defaultLegalEntity,
        };
        disclaimer = this.incrementRefCounters(isNamed, isGlobal, disclaimer, namedReference);
        this.saveDisclaimer(key, isGlobal, disclaimer);
        if (display) {
            this.updateInternal(type);
        }
        const partial = {
            text,
            type,
            reference: disclaimer.reference
                ? String(disclaimer.reference)
                : undefined,
            namedReference: isNamed ? [disclaimer.namedReference] : [],
            references: this.getReferences(disclaimer, true),
            legalEntity: legalEntity || this.defaultLegalEntity,
        };
        const un = () => {
            this.unregisterDisclaimer(partial);
        };
        return Object.assign(Object.assign({}, partial), { unregister: un });
    }
    unregisterGlobalDisclaimer(text, legalEntity) {
        this.unregisterDisclaimer({ text, type: 'global', legalEntity });
    }
    unregisterReferenceDisclaimer(kind, text, namedReference, legalEntity) {
        const named = namedReference ? [namedReference] : undefined;
        this.unregisterDisclaimer({
            text,
            type: 'reference',
            refKind: kind,
            namedReference: named,
            legalEntity,
        });
    }
    unregisterDisclaimer(disclaimer) {
        const { text, type, refKind, legalEntity } = disclaimer;
        const display = this.isTypeDisplayableInFooter(type, refKind);
        const isGlobal = type === 'global';
        const sanitizedText = this.sanitize(text);
        const key = this.convertToKey(legalEntity || this.defaultLegalEntity, sanitizedText, type, refKind);
        const isNamed = disclaimer.namedReference !== undefined;
        const existing = isGlobal
            ? this.state.globalDisclaimers[key]
            : this.state.referencedDisclaimers[key];
        if (!existing) {
            console.error(`Cannot unregister reference disclaimer ${JSON.stringify(text)} of type ${type}.`);
            return false;
        }
        let toSet = Object.assign({}, existing);
        toSet = this.decrementRefCounters(isNamed, toSet, disclaimer.namedReference && disclaimer.namedReference.length > 0
            ? disclaimer.namedReference[0]
            : undefined);
        this.updateOrDeleteDisclaimer(key, toSet, isGlobal, display);
        return true;
    }
    getLegalEntityLabels() {
        return this.legalEntityLabels;
    }
    getFooterDisclaimers() {
        return {
            global: this.getFooterGlobalDisclaimers(),
            reference: this.getFooterReferenceDisclaimers(),
        };
    }
    getLegallyGroupedDisclaimers() {
        const legallyGrouped = {};
        [
            ...this.getFooterGlobalDisclaimers(),
            ...this.getFooterReferenceDisclaimers(),
        ].forEach((disclaimer) => {
            const legalToGet = disclaimer.legalEntity || this.defaultLegalEntity;
            if (!legallyGrouped[legalToGet]) {
                legallyGrouped[legalToGet] = { global: [], reference: [] };
            }
            legallyGrouped[legalToGet][disclaimer.type].push(disclaimer);
        });
        return Object.keys(legallyGrouped).map((legal) => ({
            legalEntity: legal,
            legalEntityLabel: this.legalEntityLabels[legal],
            disclaimers: {
                global: legallyGrouped[legal].global,
                reference: legallyGrouped[legal].reference,
            },
        }));
    }
    getFooterGlobalDisclaimers() {
        return Array.from(Object.values(this.state.globalDisclaimers)).map(this.convertToDisclaimer.bind(this));
    }
    getFooterReferenceDisclaimers() {
        return Array.from(Object.values(this.state.referencedDisclaimers))
            .filter((da) => da.type === 'reference' && da.refKind === 'footer-reference')
            .map(this.convertToDisclaimer.bind(this));
    }
    updateOrDeleteDisclaimer(key, toSet, isGlobal, display) {
        if (isGlobal) {
            this.hasAnyReference(toSet)
                ? (this.state.globalDisclaimers[key] = toSet)
                : // tslint:disable-next-line:no-dynamic-delete
                    delete this.state.globalDisclaimers[key];
        }
        else {
            this.hasAnyReference(toSet)
                ? (this.state.referencedDisclaimers[key] = toSet)
                : // tslint:disable-next-line:no-dynamic-delete
                    delete this.state.referencedDisclaimers[key];
        }
        if (display) {
            this.updateInternal(toSet.type);
        }
    }
    saveDisclaimer(key, isGlobal, disclaimer) {
        // store disclaimer
        isGlobal
            ? (this.state.globalDisclaimers[key] = disclaimer)
            : (this.state.referencedDisclaimers[key] = disclaimer);
    }
    incrementRefCounters(isNamed, isGlobal, disclaimer, namedReference) {
        if (isNamed) {
            let ref = namedReference;
            const existingCount = disclaimer.referenceCount[ref];
            if (existingCount === undefined) {
                /* check if namedReference is unique across all disclaimers have different text,
                                                 if not, assign a number at the end of named reference */
                ref = this.getUniqueNamedRef(disclaimer, ref);
            }
            disclaimer.namedReference = ref;
            disclaimer.referenceCount[ref] =
                existingCount && existingCount > 0 ? existingCount + 1 : 1;
        }
        else {
            disclaimer.defaultReferenceCount = disclaimer.defaultReferenceCount + 1;
        }
        if (!isGlobal && !isNamed && !disclaimer.reference) {
            const sameTextOtherTypeDisclaimer = Object.values(this.state.referencedDisclaimers).find((dis) => dis.text === disclaimer.text &&
                dis.type !== disclaimer.type &&
                dis.reference !== undefined);
            disclaimer.reference = sameTextOtherTypeDisclaimer
                ? sameTextOtherTypeDisclaimer.reference
                : (0, next_id_1.nextId)(this.state.referencedDisclaimers);
        }
        return disclaimer;
    }
    decrementRefCounters(isNamed, disclaimer, namedReference) {
        if (isNamed) {
            const ref = namedReference;
            disclaimer.referenceCount[ref] = disclaimer.referenceCount[ref] - 1;
            if (disclaimer.referenceCount[ref] <= 0) {
                // tslint:disable-next-line:no-dynamic-delete
                delete disclaimer.referenceCount[ref];
            }
        }
        else {
            disclaimer.defaultReferenceCount = disclaimer.defaultReferenceCount - 1;
            // when disclaimer has no more references, delete it's reference
            if (disclaimer.defaultReferenceCount <= 0) {
                disclaimer.reference = undefined;
            }
        }
        return disclaimer;
    }
    getUniqueNamedRef(disclaimer, namedReference) {
        let foundNewNameRef = namedReference;
        let counter = 1;
        let isExistingWithAnotherText = true;
        while (isExistingWithAnotherText) {
            const existsWithSameNameInRef = Object.values(this.state.referencedDisclaimers).find((val) => val.referenceCount[foundNewNameRef] &&
                (val.text !== disclaimer.text || disclaimer.type === 'global'));
            const existsWithSameNameInGlobal = Object.values(this.state.globalDisclaimers).find((val) => val.referenceCount[foundNewNameRef] &&
                (val.text !== disclaimer.text || disclaimer.type !== 'global'));
            if (existsWithSameNameInGlobal || existsWithSameNameInRef) {
                counter = counter + 1;
                foundNewNameRef = `${namedReference}-${counter}`;
            }
            else {
                isExistingWithAnotherText = false;
            }
        }
        return foundNewNameRef;
    }
    updateInternal(type) {
        if (this.enabledAsyncEvent) {
            setTimeout(() => {
                this.eventEmitter.emit(`update-${type}`);
                this.eventEmitter.emit('update');
            });
        }
        else {
            this.eventEmitter.emit(`update-${type}`);
            this.eventEmitter.emit('update');
        }
    }
    hasAnyReference(disclaimer) {
        return (Object.values(disclaimer.referenceCount).find((count) => count >= 1) !==
            undefined || disclaimer.defaultReferenceCount > 0);
    }
    getReferences(dis, includeNonNamed) {
        const namedRefs = Object.keys(dis.referenceCount).filter((key) => dis.referenceCount[key] > 0);
        if (dis.reference && includeNonNamed) {
            return [String(dis.reference), ...namedRefs];
        }
        return namedRefs;
    }
    isTypeDisplayableInFooter(type, refKind) {
        return (type === 'global' ||
            (type === 'reference' && refKind === 'footer-reference'));
    }
    convertToDisclaimer(dis) {
        return {
            text: dis.text,
            type: dis.type,
            reference: dis.reference ? String(dis.reference) : undefined,
            namedReference: this.getReferences(dis, false),
            references: this.getReferences(dis, true),
            legalEntity: dis.legalEntity,
        };
    }
    convertToKey(legalEntity, text, type, refKind) {
        const ref = refKind || '|';
        return `${legalEntity}-${type}-${ref}---${text}`;
    }
}
exports.DisclaimerManagerV1Impl = DisclaimerManagerV1Impl;
//# sourceMappingURL=disclaimer-manager-v1.js.map